import React, { useState, useRef, useEffect } from 'react';
import GoogleDriveService from '../services/googleDriveService';


// Componente audio player avanzato con controlli personalizzati
const AudioMessage = ({ audioData, messageId, recordedDuration }) => {
    const [audioUrl, setAudioUrl] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(recordedDuration || 0);
    const [isLoading, setIsLoading] = useState(false);
    const audioRef = useRef(null);

    useEffect(() => {
        if (audioData) {
            // Controlla se è base64 o URL
            if (audioData.startsWith('data:audio/') || audioData.startsWith('blob:')) {
                // Verifica compatibilità iOS per i formati audio
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                             (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
                
                if (isIOS && audioData.startsWith('data:audio/webm')) {
                    console.warn('🍎 iOS: Formato WebM non supportato, audio non disponibile');
                    setAudioUrl(null);
                } else {
                    setAudioUrl(audioData);
                }
            } else if (audioData === '[AUDIO_DATA_STORED]') {
                // Audio salvato ma non disponibile localmente
                setAudioUrl(null);
            } else {
                setAudioUrl(audioData);
            }
        }
        
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            }
        };
    }, [audioData, messageId]);

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            const audioDuration = audioRef.current.duration;
            // Usa la durata dell'audio solo se è valida e non abbiamo già una durata registrata
            if (audioDuration && audioDuration !== Infinity && !isNaN(audioDuration) && !recordedDuration) {
                setDuration(audioDuration);
                console.log('🎵 Durata audio determinata dai metadata:', audioDuration);
            }
            setIsLoading(false);
        }
    };

    const handleCanPlay = () => {
        setIsLoading(false); // Audio is ready to play
    };

    const handleError = (e) => {
        const error = e.target.error;
        console.error('❌ Errore caricamento audio:', {
            code: error?.code,
            message: error?.message,
            src: e.target.src?.substring(0, 50) + '...'
        });
        
        // Gestione specifica per iOS
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                     (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        
        if (isIOS && error?.code === 4) {
            console.log('🍎 iOS: Formato audio non supportato, tentativo fallback...');
        }
        
        setIsLoading(false);
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
        }
    };

    const handlePlay = () => {
        setIsPlaying(true);
        setIsLoading(false); // Stop loading when play starts
    };
    const handlePause = () => {
        setIsPlaying(false);
        setIsLoading(false); // Stop loading when pause
    };
    const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
        setIsLoading(false); // Stop loading when ended
    };

    const togglePlayPause = (e) => {
        // Previeni la propagazione del click al form
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        if (!audioRef.current) return;
        
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            setIsLoading(true);
            
            // Timeout ridotto per iOS
            const loadingTimeout = setTimeout(() => {
                setIsLoading(false);
            }, 2000); // 2 secondi per iOS
            
            // Gestione specifica per iOS
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
            
            if (isIOS) {
                // Su iOS, prova a caricare prima di riprodurre
                audioRef.current.load();
                
                setTimeout(() => {
                    audioRef.current.play().then(() => {
                        clearTimeout(loadingTimeout);
                        setIsLoading(false);
                    }).catch(error => {
                        clearTimeout(loadingTimeout);
                        console.error('🍎 iOS: Errore riproduzione audio:', error);
                        setIsLoading(false);
                    });
                }, 100);
            } else {
                audioRef.current.play().then(() => {
                    clearTimeout(loadingTimeout);
                    setIsLoading(false);
                }).catch(error => {
                    clearTimeout(loadingTimeout);
                    console.error('Errore riproduzione audio:', error);
                    setIsLoading(false);
                });
            }
        }
    };

    const handleSeek = (e) => {
        // Previeni la propagazione del click al form
        e.preventDefault();
        e.stopPropagation();
        
        if (!audioRef.current) return;
        
        const progressBar = e.currentTarget;
        const clickX = e.nativeEvent.offsetX;
        const width = progressBar.offsetWidth;
        const newTime = (clickX / width) * duration;
        
        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const formatTime = (time) => {
        if (isNaN(time) || time === Infinity || time < 0) return '--:--';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    if (!audioUrl) {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                     (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        
        return (
            <div className="audio-message-container">
                <div className="audio-message-header">
                    <span className="audio-label">Messaggio vocale</span>
                </div>
                <div className="audio-unavailable">
                    <span>
                        {isIOS && audioData && audioData.startsWith('data:audio/webm') 
                            ? 'Formato audio non supportato su iOS' 
                            : 'Audio non disponibile localmente'}
                    </span>
                    <small>
                        {isIOS && audioData && audioData.startsWith('data:audio/webm')
                            ? 'Registra con Safari per compatibilità'
                            : 'Salvato su Google Drive'}
                    </small>
                </div>
            </div>
        );
    }

    return (
        <div>
            
            <div className="custom-audio-player">
                <button 
                    type="button"
                    className={`play-pause-btn ${isPlaying ? 'playing' : ''}`}
                    onClick={togglePlayPause}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <div className="loading-spinner">
                            <div className="spinner-dots">
                                <div></div>
                                <div></div>
                                <div></div>
                            </div>
                        </div>
                    ) : isPlaying ? (
                        <div className="pause-icon">
                            <div className="pause-bars">
                                <div></div>
                                <div></div>
                            </div>
                        </div>
                    ) : (
                        <div className="play-icon">
                            <div className="play-triangle"></div>
                        </div>
                    )}
                </button>
                
                <div className="progress-container" onClick={handleSeek}>
                    <div className="progress-bar">
                        <div 
                            className="progress-fill"
                            style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                        ></div>
                    </div>
                </div>
                
                <div className="time-display">
                    <span className="current-time">{formatTime(currentTime)}</span>
                </div>
            </div>
            
            {/* Audio element nascosto */}
            <audio 
                ref={audioRef}
                src={audioUrl}
                preload="metadata"
                onLoadedMetadata={handleLoadedMetadata}
                onCanPlay={handleCanPlay}
                onError={handleError}
                onTimeUpdate={handleTimeUpdate}
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={handleEnded}
                style={{ display: 'none' }}
            />
        </div>
    );
};

const GuestBook = () => {
    const [activeTab, setActiveTab] = useState('write');
    const [messages, setMessages] = useState([]);
    const [filteredMessages, setFilteredMessages] = useState([]);
    const [messageFilter, setMessageFilter] = useState('all'); // 'all', 'write', 'audio', 'photo'
    const [searchTerm, setSearchTerm] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioBase64, setAudioBase64] = useState(null);
    const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        message: '',
        photos: [] // Cambiato da photo singola a photos array
    });
    const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', 'uploading'
    const [isUploading, setIsUploading] = useState(false);
    const [uploadMessage, setUploadMessage] = useState('');
    const [isProcessingPhotos, setIsProcessingPhotos] = useState(false);
    const [photoProcessingProgress, setPhotoProcessingProgress] = useState({ current: 0, total: 0 });
    
    const mediaRecorderRef = useRef(null);
    const audioStreamRef = useRef(null);
    const timerRef = useRef(null);
    const fileInputRef = useRef(null);
    const driveServiceRef = useRef(null);
    const formHeaderRef = useRef(null);

    // Initialize Google Drive Service
    useEffect(() => {
        // Sostituisci con l'URL del tuo Google Apps Script Web App
        const webAppUrl = 'https://script.google.com/macros/s/AKfycbxwWW1cKJ7NKerxJ3LvvE6EoGkGxZBdecC_AuG1tqQy7sNRbU060ZnbCLXu5PqJZhFX/exec';
        
        try {
            driveServiceRef.current = new GoogleDriveService(webAppUrl);
            
            // Test immediato di connettività
            console.log('🔧 Testando connessione Google Apps Script...');
            console.log('🔗 URL nuovo:', webAppUrl);
            
            driveServiceRef.current.testConnection()
                .then(result => {
                    console.log('✅ Google Apps Script connesso:', result);
                })
                .catch(error => {
                    console.error('❌ Google Apps Script non raggiungibile:', error);
                    console.log('💡 Verifica che il Web App sia pubblicato e accessibile');
                    console.log('🔗 URL testato:', webAppUrl);
                });
            
            // Pulisci upload vecchi all'avvio
            driveServiceRef.current.cleanOldPendingUploads();
            
        } catch (initError) {
            console.error('❌ Errore inizializzazione GoogleDriveService:', initError);
        }
    }, []);

    // Load existing messages from localStorage
    useEffect(() => {
        const savedMessages = localStorage.getItem('guestBookMessages');
        if (savedMessages) {
            setMessages(JSON.parse(savedMessages));
        }
    }, []);

    // Filter messages when messages, filter, or search term change
    useEffect(() => {
        let filtered = messages;

        // Apply type filter
        if (messageFilter !== 'all') {
            filtered = filtered.filter(msg => msg.type === messageFilter);
        }

        // Apply search filter
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(msg => 
                msg.name.toLowerCase().includes(searchLower) ||
                (msg.message && msg.message.toLowerCase().includes(searchLower))
            );
        }

        setFilteredMessages(filtered);
    }, [messages, messageFilter, searchTerm]);

    // Cleanup audio URL when component unmounts or audio changes
    useEffect(() => {
        return () => {
            if (audioPreviewUrl) {
                URL.revokeObjectURL(audioPreviewUrl);
            }
        };
    }, [audioPreviewUrl]);

    // Cleanup all resources when component unmounts
    useEffect(() => {
        return () => {
            // Stop recording if in progress
            if (isRecording && mediaRecorderRef.current) {
                mediaRecorderRef.current.stop();
            }
            
            // Clear timer
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
            
            // Stop audio stream
            if (audioStreamRef.current) {
                audioStreamRef.current.getTracks().forEach(track => track.stop());
            }
            
            // Cleanup preview URL
            if (audioPreviewUrl) {
                URL.revokeObjectURL(audioPreviewUrl);
            }
        };
    }, []); // Empty dependency array means this runs only on unmount

    // Save messages to localStorage with quota management
    const saveMessages = (newMessages) => {
        try {
            // Limita il numero di messaggi salvati localmente per evitare quota exceeded
            const maxMessages = 50;
            const messagesToSave = newMessages.slice(0, maxMessages);
            
            // Riduci la dimensione dei dati audio/foto per il salvataggio locale
            const lightMessages = messagesToSave.map(msg => ({
                ...msg,
                // Mantieni solo un riferimento ai file grandi invece dei dati completi
                audioData: msg.audioData ? '[AUDIO_DATA_STORED]' : null,
                photos: msg.photos?.length > 0 ? '[PHOTOS_DATA_STORED]' : (msg.photo ? '[PHOTO_DATA_STORED]' : null),
                hasAudio: !!msg.audioData,
                hasPhoto: !!(msg.photos?.length > 0 || msg.photo)
            }));
            
            localStorage.setItem('guestBookMessages', JSON.stringify(lightMessages));
            setMessages(newMessages); // Usa i dati completi in memoria
        } catch (error) {
            console.error('Errore salvataggio localStorage:', error);
            
            // Se fallisce, prova a salvare solo gli ultimi 20 messaggi
            try {
                const minimalMessages = newMessages.slice(0, 20).map(msg => ({
                    id: msg.id,
                    name: msg.name,
                    type: msg.type,
                    message: msg.message,
                    timestamp: msg.timestamp,
                    hasAudio: !!msg.audioData,
                    hasPhoto: !!(msg.photos?.length > 0 || msg.photo)
                }));
                
                localStorage.setItem('guestBookMessages', JSON.stringify(minimalMessages));
            } catch (finalError) {
                // Pulisci localStorage e riprova
                localStorage.removeItem('guestBookMessages');
                localStorage.removeItem('pendingGuestBookUploads');
            }
            
            setMessages(newMessages); // Usa i dati completi in memoria comunque
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle multiple photo upload with compression
    const handlePhotoUpload = async (e) => {
        const files = Array.from(e.target.files); // Supporta selezione multipla
        const maxPhotos = 15; // Limite massimo di foto per messaggio
        
        if (formData.photos.length + files.length > maxPhotos) {
            alert(`Puoi caricare massimo ${maxPhotos} foto per messaggio. Hai selezionato ${files.length} foto, ma ne hai già ${formData.photos.length}.`);
            return;
        }
        
        // Se ci sono molte foto, mostra il loader
        if (files.length > 3) {
            setIsProcessingPhotos(true);
            setPhotoProcessingProgress({ current: 0, total: files.length });
        }
        
        // Processa ogni file selezionato
        const processedPhotos = [];
        
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const fileSizeMB = file.size / 1024 / 1024;
            
            // Aggiorna il progresso
            if (files.length > 3) {
                setPhotoProcessingProgress({ current: i + 1, total: files.length });
            }
            
            try {
                const processedPhoto = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        const img = new Image();
                        img.onload = () => {
                            // Comprimi se l'immagine è troppo grande
                            const canvas = document.createElement('canvas');
                            const ctx = canvas.getContext('2d');
                            
                            // Ridimensiona per upload ultra-veloci (max 1000px)
                            let { width, height } = img;
                            const maxSize = 1000;
                            
                            if (width > maxSize || height > maxSize) {
                                if (width > height) {
                                    height = (height * maxSize) / width;
                                    width = maxSize;
                                } else {
                                    width = (width * maxSize) / height;
                                    height = maxSize;
                                }
                            }
                            
                            canvas.width = width;
                            canvas.height = height;
                            ctx.drawImage(img, 0, 0, width, height);
                            
                            // Compressione ultra-aggressiva per upload istantanei
                            let quality = 0.6;
                            if (fileSizeMB > 2) quality = 0.4;
                            else if (fileSizeMB > 1) quality = 0.5;
                            
                            const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
                            
                            // Log info compressione
                            const originalSize = file.size;
                            const compressedSize = Math.round((compressedDataUrl.length * 3) / 4);
                            console.log('📸 Compressione foto:', {
                                fileName: file.name,
                                originalSizeKB: Math.round(originalSize / 1024),
                                compressedSizeKB: Math.round(compressedSize / 1024),
                                compressionRatio: Math.round((1 - compressedSize / originalSize) * 100) + '%',
                                quality: quality
                            });
                            
                            resolve({
                                id: Date.now() + Math.random() + i, // ID unico per ogni foto
                                data: compressedDataUrl,
                                name: file.name
                            });
                        };
                        img.onerror = () => reject(new Error(`Errore nel caricamento dell'immagine: ${file.name}`));
                        img.src = e.target.result;
                    };
                    reader.onerror = () => reject(new Error(`Errore nella lettura del file: ${file.name}`));
                    reader.readAsDataURL(file);
                });
                
                processedPhotos.push(processedPhoto);
                
            } catch (error) {
                console.error('Errore nel processamento della foto:', error);
                // Continua con le altre foto anche se una fallisce
            }
        }
        
        // Aggiungi tutte le foto processate al form
        if (processedPhotos.length > 0) {
            setFormData(prev => ({
                ...prev,
                photos: [...prev.photos, ...processedPhotos]
            }));
        }
        
        // Nascondi il loader
        setIsProcessingPhotos(false);
        setPhotoProcessingProgress({ current: 0, total: 0 });
    };

    // Rimuovi una foto specifica dall'array
    const removePhoto = (photoId) => {
        setFormData(prev => ({
            ...prev,
            photos: prev.photos.filter(photo => photo.id !== photoId)
        }));
    };

    // Rimuovi tutte le foto
    const removeAllPhotos = () => {
        if (formData.photos.length > 0) {
            const confirmMessage = formData.photos.length === 1 
                ? 'Sei sicuro di voler eliminare questa foto?' 
                : `Sei sicuro di voler eliminare tutte le ${formData.photos.length} foto?`;
            
            if (window.confirm(confirmMessage)) {
                setFormData(prev => ({
                    ...prev,
                    photos: []
                }));
            }
        }
    };

    // Convert blob to base64 - versione ultra-veloce per performance ottimali
    const blobToBase64 = (blob) => {
        return new Promise((resolve, reject) => {
            // Validazione minima per massima velocità
            if (!blob || blob.size === 0) {
                reject(new Error('Blob non valido'));
                return;
            }
            
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error('Errore lettura file'));
            reader.readAsDataURL(blob);
        });
    };







    // Start audio recording
    const startRecording = async () => {
        try {
            // Cleanup any existing streams first
            if (audioStreamRef.current) {
                audioStreamRef.current.getTracks().forEach(track => track.stop());
                audioStreamRef.current = null;
            }

            const stream = await navigator.mediaDevices.getUserMedia({ 
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 44100
                }
            });
            audioStreamRef.current = stream;
            
            // Scegli il formato migliore supportato dal browser con impostazioni ottimizzate per velocità
            const options = {
                audioBitsPerSecond: 96000 // 96kbps per upload più veloce mantenendo qualità accettabile
            };
            
            // Rilevamento iOS per gestione specifica
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
            
            console.log('📱 Dispositivo rilevato:', isIOS ? 'iOS/Safari' : 'Altri');
            
            // Priorità ai formati compatibili con iOS per una migliore riproduzione
            if (isIOS) {
                // Su iOS, usa solo formati garantiti per funzionare
                if (MediaRecorder.isTypeSupported('audio/mp4')) {
                    options.mimeType = 'audio/mp4';
                    console.log('🍎 iOS rilevato: usando audio/mp4 (formato ottimale)');
                } else if (MediaRecorder.isTypeSupported('audio/wav')) {
                    options.mimeType = 'audio/wav';
                    console.log('🍎 iOS rilevato: usando audio/wav (fallback)');
                } else {
                    console.warn('🍎 iOS: Nessun formato compatibile supportato!');
                }
            } else {
                // Su altri browser, usa formati compressi per velocità
                if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
                    options.mimeType = 'audio/webm;codecs=opus';
                } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
                    options.mimeType = 'audio/mp4';
                } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
                    options.mimeType = 'audio/ogg;codecs=opus';
                } else if (MediaRecorder.isTypeSupported('audio/webm')) {
                    options.mimeType = 'audio/webm';
                } else if (MediaRecorder.isTypeSupported('audio/wav')) {
                    options.mimeType = 'audio/wav';
                } else {
                    console.warn('⚠️ Nessun formato compatibile, useremo conversione');
                }
            }
            
            const mediaRecorder = new MediaRecorder(stream, options);
            mediaRecorderRef.current = mediaRecorder;
            
            console.log('🎤 MediaRecorder inizializzato con:', options.mimeType || 'formato default');
            
            const chunks = [];
            
            // Eventi di debug per MediaRecorder
            mediaRecorder.onstart = () => {
                console.log('🎬 Registrazione iniziata');
            };
            
            mediaRecorder.onpause = () => {
                console.log('⏸️ Registrazione in pausa');
            };
            
            mediaRecorder.onresume = () => {
                console.log('▶️ Registrazione ripresa');
            };
            
            mediaRecorder.onerror = (event) => {
                console.error('❌ Errore MediaRecorder:', event.error);
            };
            
            // Gestione migliorata dei chunk audio
            mediaRecorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) {
                    chunks.push(e.data);
                    console.log(`📦 Chunk ricevuto: ${e.data.size} bytes, totale chunks: ${chunks.length}`);
                } else {
                    console.warn('⚠️ Chunk vuoto ricevuto');
                }
            };
            
            mediaRecorder.onstop = async () => {
                console.log(`🎵 === REGISTRAZIONE FERMATA ===`);
                console.log(`📊 Chunks totali ricevuti: ${chunks.length}`);
                
                // Resetta lo stato di registrazione QUI per una transizione fluida
                setIsRecording(false);
                
                if (chunks.length === 0) {
                    console.error('❌ Nessun chunk audio ricevuto!');
                    console.error('💡 Possibili cause:');
                    console.error('  - MediaRecorder non ha registrato dati');
                    console.error('  - Browser non supporta il formato scelto');
                    console.error('  - Microfono non accessibile');
                    alert('Errore nella registrazione audio. Riprova.');
                    return;
                }
                
                // Verifica che i chunks abbiano contenuto
                let totalSize = 0;
                chunks.forEach((chunk, index) => {
                    console.log(`📦 Chunk ${index}: ${chunk.size} bytes, tipo: ${chunk.type}`);
                    totalSize += chunk.size;
                });
                
                if (totalSize === 0) {
                    console.error('❌ Tutti i chunks sono vuoti!');
                    alert('Errore: registrazione audio vuota. Riprova.');
                    return;
                }
                
                console.log(`📊 Dimensione totale chunks: ${totalSize} bytes`);
                
                // Usa il tipo MIME corretto dal MediaRecorder
                const mimeType = mediaRecorder.mimeType || 'audio/webm';
                console.log('🔍 MIME type determinato:', mimeType);
                
                // Crea il blob con header corretti
                let blob;
                try {
                    if (mimeType.includes('webm')) {
                        // Per WebM, assicuriamoci che abbia i metadata corretti
                        const options = { type: mimeType };
                        blob = new Blob(chunks, options);
                    } else {
                        blob = new Blob(chunks, { type: mimeType });
                    }
                    
                    console.log('✅ Blob creato con successo:', {
                        size: blob.size,
                        sizeKB: `${(blob.size / 1024).toFixed(2)}KB`,
                        type: blob.type,
                        duration: `${recordingTime}s`,
                        chunks: chunks.length,
                        constructor: blob.constructor.name
                    });
                    
                    // Verifica che il blob sia valido
                    if (blob.size === 0) {
                        throw new Error('Blob creato ha dimensione 0');
                    }
                    
                    if (blob.size < 100) {
                        console.warn('⚠️ Blob molto piccolo - possibile problema nella registrazione');
                    }
                    
                } catch (blobError) {
                    console.error('❌ Errore nella creazione del blob:', blobError);
                    alert('Errore nella creazione del file audio. Riprova la registrazione.');
                    return;
                }
                
                // Mantieni l'audio originale senza tagli
                let finalBlob = blob;
                setAudioBlob(blob);
                console.log('✅ Audio mantenuto intatto:', recordingTime, 's');
                
                // Test immediato del blob creando un URL temporaneo
                try {
                    const testUrl = URL.createObjectURL(finalBlob);
                    console.log('✅ Test URL blob riuscito:', testUrl.substring(0, 50) + '...');
                    
                    // Cleanup immediato del test URL
                    setTimeout(() => URL.revokeObjectURL(testUrl), 1000);
                } catch (urlError) {
                    console.error('❌ Test URL blob fallito:', urlError);
                    alert('Errore: file audio corrotto. Riprova la registrazione.');
                    return;
                }
                
                // Aspetta un momento per assicurarsi che il blob sia completamente formato
                setTimeout(() => {
                    try {
                        // Create stable URL for preview usando il blob corretto
                        if (audioPreviewUrl) {
                            URL.revokeObjectURL(audioPreviewUrl);
                        }
                        const newPreviewUrl = URL.createObjectURL(finalBlob);
                        setAudioPreviewUrl(newPreviewUrl);
                        console.log('🔗 URL preview creato:', newPreviewUrl.substring(0, 50) + '...');
                        
                        console.log('✅ Audio pronto per l\'anteprima e l\'upload');
                    } catch (previewError) {
                        console.error('❌ Errore creazione preview URL:', previewError);
                        // Non bloccare il processo per questo errore
                        console.log('⚠️ Preview non disponibile, ma audio dovrebbe essere valido per upload');
                    }
                }, 300); // Delay ridotto
            };
            
            // Avvia la registrazione SENZA timeslice per evitare tagli
            mediaRecorder.start(); // Raccoglie chunk solo quando si ferma
            setIsRecording(true);
            setRecordingTime(0);
            
            // Start timer
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
            
        } catch (error) {
            console.error('Error accessing microphone:', error);
            
            // Cleanup in case of error
            setIsRecording(false);
            setRecordingTime(0);
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            if (audioStreamRef.current) {
                audioStreamRef.current.getTracks().forEach(track => track.stop());
                audioStreamRef.current = null;
            }
            
            // Rilevamento iOS per messaggi di errore specifici
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
            
            let errorMessage = 'Impossibile accedere al microfono. ';
            if (error.name === 'NotAllowedError') {
                if (isIOS) {
                    errorMessage += 'Su iOS/Safari: Vai su Impostazioni > Safari > Microfono e consenti l\'accesso per questo sito.';
                } else {
                    errorMessage += 'Autorizza l\'accesso al microfono nelle impostazioni del browser.';
                }
            } else if (error.name === 'NotFoundError') {
                if (isIOS) {
                    errorMessage += 'Microfono non disponibile. Verifica che non sia usato da altre app.';
                } else {
                    errorMessage += 'Nessun microfono trovato. Verifica che sia collegato.';
                }
            } else if (error.name === 'NotSupportedError') {
                if (isIOS) {
                    errorMessage += 'Versione di Safari non supportata. Aggiorna iOS alla versione più recente.';
                } else {
                    errorMessage += 'Il tuo browser non supporta la registrazione audio.';
                }
            } else {
                if (isIOS) {
                    errorMessage += `Errore iOS: ${error.message}. Prova a riavviare Safari o il dispositivo.`;
                } else {
                    errorMessage += 'Errore: ' + error.message;
                }
            }
            
            alert(errorMessage);
        }
    };

    // Stop audio recording
    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            console.log('🛑 Fermando registrazione...');
            
            // Ferma immediatamente la registrazione per evitare perdita di dati
            if (mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
                console.log('⏹️ MediaRecorder fermato');
            }
            
            // Ferma lo stream audio
            if (audioStreamRef.current) {
                audioStreamRef.current.getTracks().forEach(track => {
                    track.stop();
                    console.log('🔇 Track audio fermato');
                });
            }
            
            // NON resettare subito isRecording - lascia che l'evento onstop gestisca la transizione
            // setIsRecording(false); // Rimosso per evitare flash visivo
            clearInterval(timerRef.current);
        }
    };

    // Start new recording (clean up previous)
    const startNewRecording = () => {
        // Avvia immediatamente la nuova registrazione senza resettare prima
        startRecording();
        
        // Clean up previous recording dopo aver avviato la nuova
        setTimeout(() => {
            setAudioBlob(null);
            setAudioBase64(null);
            if (audioPreviewUrl) {
                URL.revokeObjectURL(audioPreviewUrl);
                setAudioPreviewUrl(null);
            }
        }, 100); // Piccolo delay per evitare il flash del bottone
    };

    // Format recording time
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Validate form based on active tab
    const isFormValid = () => {
        // Nome sempre richiesto
        if (!formData.name.trim()) return false;
        
        // Non permettere invio se è in corso un upload
        if (isUploading) return false;
        
        // Validazione specifica per tab
        switch (activeTab) {
            case 'write':
                return formData.message.trim().length > 0;
            case 'audio':
                return audioBlob !== null;
            case 'photo':
                return formData.photos.length > 0;
            default:
                return false;
        }
    };

    // Reset audio recording state completely
    const resetAudioState = () => {
        setAudioBlob(null);
        setAudioBase64(null);
        setRecordingTime(0);
        if (audioPreviewUrl) {
            URL.revokeObjectURL(audioPreviewUrl);
            setAudioPreviewUrl(null);
        }
        
        // Stop any ongoing recording
        if (isRecording && mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            clearInterval(timerRef.current);
        }
        
        // Stop audio stream
        if (audioStreamRef.current) {
            audioStreamRef.current.getTracks().forEach(track => track.stop());
            audioStreamRef.current = null;
        }
    };

    // Generate success message based on active tab
    const getSuccessMessage = (isDuplicate = false) => {
        if (isDuplicate) {
            switch (activeTab) {
                case 'audio':
                    return 'Audio già salvato - duplicato evitato!';
                case 'photo':
                    return 'Foto già salvata - duplicato evitato!';
                default:
                    return 'Messaggio già salvato - duplicato evitato!';
            }
        }
        
        switch (activeTab) {
            case 'audio':
                return 'Il tuo audio è stato caricato con successo!';
            case 'photo':
                return 'Upload effettuato con successo!';
            case 'write':
            default:
                return 'Il tuo messaggio è stato caricato con successo!';
        }
    };



    // Submit message
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Previeni doppi submit
        if (isUploading) {
            console.log('⚠️ Upload già in corso, ignorando submit duplicato');
            return;
        }
        
        // Protezione aggiuntiva contro invii accidentali
        if (!isFormValid()) {
            console.log('⚠️ Form non valido, ignorando submit');
            return;
        }
        
        if (!formData.name.trim()) {
            alert('Per favore inserisci il tuo nome!');
            return;
        }

        if (activeTab === 'write' && !formData.message.trim()) {
            alert('Per favore scrivi un messaggio!');
            return;
        }

        if (activeTab === 'audio' && !audioBlob) {
            alert('Per favore registra un messaggio audio!');
            return;
        }

        // Scroll all'inizio del form per mostrare il caricamento
        if (formHeaderRef.current) {
            formHeaderRef.current.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start',
                inline: 'nearest'
            });
        }

        console.log('🚀 Inizio processo di submit');
        
        setIsUploading(true);
        setSubmitStatus('uploading');
        setUploadMessage('Salvando il messaggio...');

        // Prepara i dati del messaggio
        let audioDataForMessage = null;
        if (activeTab === 'audio' && audioBlob) {
            const startTime = performance.now();
            setUploadMessage('Preparando audio per l\'upload...');
            
            try {
                let finalAudioBlob = audioBlob;
                
                // Per velocità ottimale, accettiamo tutti i formati moderni senza conversione
                // La conversione rallenta l'upload - meglio inviare il formato originale
                console.log('⚡ Formato audio:', audioBlob.type, '- invio diretto per velocità ottimale');
                
                // Skip conversione per massima velocità - Google Drive gestisce la maggior parte dei formati
                finalAudioBlob = audioBlob;
                
                setUploadMessage('Codificando audio per l\'invio...');
                
                // Prova la conversione normale, con fallback se fallisce
                try {
                    audioDataForMessage = await blobToBase64(finalAudioBlob);
                } catch (blobConversionError) {
                    console.error('❌ Conversione blob normale fallita:', blobConversionError);
                    console.log('🔄 Tentativo conversione con metodo alternativo...');
                    
                    try {
                        // Rilevamento iOS per gestione specifica degli errori
                        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                                     (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
                        
                        if (isIOS) {
                            console.log('🍎 Errore su iOS rilevato, usando strategia specifica...');
                        }
                        
                        // Metodo alternativo: usa ArrayBuffer
                        const arrayBuffer = await finalAudioBlob.arrayBuffer();
                        const bytes = new Uint8Array(arrayBuffer);
                        
                        let binaryString = '';
                        
                        // Su iOS, usa conversione chunk-based per evitare limiti di stack
                        if (isIOS) {
                            console.log('🍎 Usando conversione chunk-based per iOS...');
                            const chunkSize = 8192;
                            for (let i = 0; i < bytes.length; i += chunkSize) {
                                const chunk = bytes.slice(i, i + chunkSize);
                                binaryString += String.fromCharCode.apply(null, chunk);
                            }
                        } else {
                            for (let i = 0; i < bytes.length; i++) {
                                binaryString += String.fromCharCode(bytes[i]);
                            }
                        }
                        
                        const base64 = btoa(binaryString);
                        audioDataForMessage = `data:${finalAudioBlob.type};base64,${base64}`;
                        
                        console.log('✅ Conversione alternativa riuscita:', {
                            platform: isIOS ? 'iOS' : 'Altri',
                            arrayBufferSize: arrayBuffer.byteLength,
                            base64Length: base64.length,
                            finalLength: audioDataForMessage.length
                        });
                        
                    } catch (alternativeError) {
                        console.error('❌ Anche la conversione alternativa è fallita:', alternativeError);
                        
                        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                                     (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
                        
                        if (isIOS) {
                            throw new Error('Errore specifico iOS: Impossibile processare l\'audio. Prova a registrare un messaggio più breve o riavvia Safari.');
                        } else {
                            throw new Error('Impossibile convertire l\'audio in formato base64. Prova a registrare di nuovo.');
                        }
                    }
                }
                
                // Verifica che i dati base64 siano validi
                console.log('🔍 Verifica dati base64:', {
                    length: audioDataForMessage.length,
                    startsWithData: audioDataForMessage.startsWith('data:audio/'),
                    firstChars: audioDataForMessage.substring(0, 50),
                    lastChars: audioDataForMessage.substring(audioDataForMessage.length - 20)
                });
                
                // Debug specifico per il formato data URL
                if (audioDataForMessage.startsWith('data:')) {
                    const commaIndex = audioDataForMessage.indexOf(',');
                    if (commaIndex > 0) {
                        const header = audioDataForMessage.substring(0, commaIndex + 1);
                        console.log('🔍 Header data URL completo:', header);
                        console.log('🔍 Analisi header:', {
                            hasBase64: header.includes('base64'),
                            hasCodecs: header.includes('codecs'),
                            mimeType: header.match(/data:([^;,]+)/)?.[1] || 'non trovato'
                        });
                    }
                }
                
                // Test di validità base64 e pulizia MIGLIORATO
                try {
                    console.log('🔍 === VALIDAZIONE BASE64 FRONTEND ===');
                    
                    // Verifica formato data URL
                    if (!audioDataForMessage.startsWith('data:audio/')) {
                        throw new Error('Formato data URL non valido - deve iniziare con data:audio/');
                    }
                    
                    const parts = audioDataForMessage.split(',');
                    if (parts.length !== 2) {
                        throw new Error('Formato data URL malformato - deve contenere esattamente una virgola');
                    }
                    
                    const mimeType = parts[0];
                    const base64Part = parts[1];
                    
                    console.log('🔍 MIME type:', mimeType);
                    console.log('🔍 Base64 lunghezza originale:', base64Part.length);
                    console.log('🔍 Base64 primi 50 char:', base64Part.substring(0, 50));
                    console.log('🔍 Base64 ultimi 20 char:', base64Part.substring(base64Part.length - 20));
                    
                    // Verifica che ci sia contenuto
                    if (!base64Part || base64Part.length === 0) {
                        throw new Error('Nessun contenuto base64 trovato dopo la virgola');
                    }
                    
                    // Pulisci il base64 da caratteri problematici (TRIPLA PULIZIA per massima sicurezza)
                    let cleanBase64 = base64Part;
                    
                    // 1. Rimuovi tutti i tipi di whitespace
                    cleanBase64 = cleanBase64.replace(/\s/g, '');
                    
                    // 2. Rimuovi caratteri non base64
                    cleanBase64 = cleanBase64.replace(/[^A-Za-z0-9+/=]/g, '');
                    
                    // 3. Assicurati che il padding sia corretto
                    const paddingNeeded = 4 - (cleanBase64.length % 4);
                    if (paddingNeeded !== 4) {
                        cleanBase64 += '='.repeat(paddingNeeded);
                    }
                    
                    console.log('🧹 Pulizia completata:');
                    console.log('  - Lunghezza dopo pulizia:', cleanBase64.length);
                    console.log('  - Padding aggiunto:', paddingNeeded !== 4 ? paddingNeeded : 0, 'caratteri');
                    console.log('  - Multiplo di 4:', cleanBase64.length % 4 === 0);
                    
                    // Verifica finale della lunghezza
                    if (cleanBase64.length % 4 !== 0) {
                        throw new Error(`Lunghezza base64 non valida: ${cleanBase64.length} (deve essere multiplo di 4)`);
                    }
                    
                    // Test decodifica progressivo per identificare il punto esatto dell'errore
                    try {
                        // Test con campione piccolo
                        const testSample = cleanBase64.substring(0, Math.min(100, cleanBase64.length));
                        if (testSample.length >= 4) {
                            const testDecode = atob(testSample);
                            console.log('✅ Test decodifica campione riuscito:', testDecode.length, 'bytes');
                        }
                        
                        // Test con campione medio (se abbastanza grande)
                        if (cleanBase64.length > 1000) {
                            const mediumSample = cleanBase64.substring(0, 1000);
                            const mediumDecode = atob(mediumSample);
                            console.log('✅ Test decodifica campione medio riuscito:', mediumDecode.length, 'bytes');
                        }
                        
                        // Test decodifica completa
                        const fullDecode = atob(cleanBase64);
                        console.log('✅ Test decodifica completa riuscito:', fullDecode.length, 'bytes');
                        
                        // Verifica che i dati abbiano senso
                        if (fullDecode.length === 0) {
                            throw new Error('Decodifica ha prodotto 0 bytes - dati vuoti');
                        }
                        
                        if (fullDecode.length < 100) {
                            console.warn('⚠️ File audio molto piccolo (' + fullDecode.length + ' bytes)');
                        }
                        
                    } catch (decodeTestError) {
                        console.error('❌ Test decodifica fallito:', decodeTestError);
                        console.error('📋 Base64 problematico (primi 200 char):', cleanBase64.substring(0, 200));
                        throw new Error('Base64 non valido - impossibile decodificare: ' + decodeTestError.message);
                    }
                    
                    // Se il base64 è stato modificato, aggiorna i dati
                    if (cleanBase64 !== base64Part) {
                        audioDataForMessage = mimeType + ',' + cleanBase64;
                        console.log('🔧 Base64 pulito e corretto - differenze trovate e risolte');
                        console.log('  - Lunghezza originale:', base64Part.length);
                        console.log('  - Lunghezza pulita:', cleanBase64.length);
                    } else {
                        console.log('✅ Base64 già pulito - nessuna modifica necessaria');
                    }
                    
                    // Verifica finale del formato completo
                    if (!audioDataForMessage.includes(',') || audioDataForMessage.split(',').length !== 2) {
                        throw new Error('Data URL finale malformato');
                    }
                    
                    console.log('✅ Validazione base64 completata con successo');
                    
                } catch (base64Error) {
                    console.error('❌ Errore validazione base64:', base64Error);
                    console.error('📋 Stack trace:', base64Error.stack);
                    throw new Error('Dati audio corrotti durante la codifica: ' + base64Error.message);
                }
                
                const totalTime = performance.now() - startTime;
                console.log('⏱️ Preparazione audio completata in ' + Math.round(totalTime) + 'ms');
                console.log('✅ Audio preparato per upload:', {
                    type: finalAudioBlob.type,
                    originalSize: audioBlob.size,
                    finalSize: finalAudioBlob.size,
                    base64Length: audioDataForMessage.length,
                    base64SizeKB: Math.round(audioDataForMessage.length / 1024),
                    base64Preview: audioDataForMessage.substring(0, 50) + '...'
                });
                
                // Controlla se il file è troppo grande per Google Apps Script
                if (audioDataForMessage.length > 5000000) { // 5MB
                    console.warn('⚠️ File audio molto grande (' + Math.round(audioDataForMessage.length/1024) + 'KB), potrebbe fallire su Google Apps Script');
                }
            } catch (error) {
                console.error('❌ Errore preparazione audio:', error);
                setSubmitStatus('error');
                setUploadMessage('Errore nella preparazione dell\'audio. Riprova la registrazione.');
                setIsUploading(false);
                setTimeout(() => {
                    setSubmitStatus(null);
                    setUploadMessage('');
                }, 5000);
                return;
            }
        }

        const newMessage = {
            id: Date.now(),
            name: formData.name,
            type: activeTab,
            timestamp: new Date().toLocaleString('it-IT'),
            message: formData.message,
            audioData: audioDataForMessage,
            photos: formData.photos,
            recordedDuration: activeTab === 'audio' ? recordingTime : null
        };

        try {
            // Salva localmente immediatamente
            const updatedMessages = [newMessage, ...messages];
            saveMessages(updatedMessages);

            // Carica su Google Drive
            if (driveServiceRef.current) {
                // Messaggio di stato personalizzato per tipo di contenuto con emoji
                if (activeTab === 'photo') {
                    setUploadMessage('Sto effettuando l\'upload...');
                } else if (activeTab === 'audio') {
                    setUploadMessage('Sto caricando l\'audio...');
                } else {
                    setUploadMessage('Sto caricando il messaggio...');
                }
                
                console.log('📤 Inizio upload su Google Drive...');
                console.log('📊 Dati messaggio per upload:', {
                    id: newMessage.id,
                    name: newMessage.name,
                    type: newMessage.type,
                    hasAudio: !!newMessage.audioData,
                    audioDataLength: newMessage.audioData ? newMessage.audioData.length : 0
                });
                
                const driveResult = await driveServiceRef.current.uploadGuestBookMessage(newMessage);
                
                if (driveResult.status === 'success') {
                    setSubmitStatus('success');
                    if (driveResult.duplicate) {
                        setUploadMessage(getSuccessMessage(true));
                        console.log('📝 Duplicato evitato dal server per:', newMessage.id);
                    } else {
                        setUploadMessage(getSuccessMessage(false));
                        console.log('🎉 Upload completato con successo:', newMessage);
                    }
                } else if (driveResult.status === 'duplicate') {
                    setSubmitStatus('success');
                    setUploadMessage(getSuccessMessage(true));
                    console.log('📝 Upload duplicato evitato per:', newMessage.id);
                } else if (driveResult.status === 'pending') {
                    setSubmitStatus('success');
                    const baseMessage = getSuccessMessage(false).replace(' con successo!', '');
                    setUploadMessage(`${baseMessage} salvato! Sarà caricato online quando tornerai connesso.`);
                } else {
                    setSubmitStatus('success');
                    const baseMessage = getSuccessMessage(false).replace(' è stato caricato con successo!', '');
                    setUploadMessage(`${baseMessage} salvato localmente.`);
                }
            }

            // Reset form and cleanup
            setFormData({ name: '', message: '', photos: [] });
            resetAudioState(); // Complete audio reset
            
            setTimeout(() => {
                setSubmitStatus(null);
                setUploadMessage('');
            }, 2000); // Ridotto a 2s come richiesto

        } catch (error) {
            console.error('❌ Errore nel salvataggio completo:', error);
            console.error('📋 Stack trace:', error.stack);
            console.error('📋 Messaggio errore:', error.message);
            
            setSubmitStatus('error');
            
            // Messaggio di errore più specifico e dettagliato
            let errorMessage = 'Si è verificato un errore durante il salvataggio.';
            
            if (error.message) {
                if (error.message.includes('decodificare la stringa') || error.message.includes('Impossibile decodificare')) {
                    errorMessage = 'Errore nel formato audio. Prova a registrare di nuovo il messaggio vocale.';
                } else if (error.message.includes('Base64 non valido') || error.message.includes('corrotti durante la codifica')) {
                    errorMessage = 'Dati audio danneggiati. Registra nuovamente il messaggio vocale.';
                } else if (error.message.includes('Google Apps Script') || error.message.includes('fetch')) {
                    errorMessage = 'Errore di connessione con Google Drive. Verifica la connessione internet e riprova.';
                } else if (error.message.includes('network') || error.message.includes('NetworkError')) {
                    errorMessage = 'Problema di rete. Controlla la connessione internet e riprova.';
                } else if (error.message.includes('timeout') || error.message.includes('Timeout')) {
                    errorMessage = 'Caricamento troppo lento. Riprova con una connessione più stabile.';
                } else if (error.message.includes('quota') || error.message.includes('storage')) {
                    errorMessage = 'Spazio di archiviazione insufficiente. Contatta l\'amministratore.';
                } else {
                    errorMessage = `Errore: ${error.message}`;
                }
            }
            
            setUploadMessage(errorMessage);
            setTimeout(() => {
                setSubmitStatus(null);
                setUploadMessage('');
            }, 4000); // Ridotto da 8s a 4s - sufficiente per leggere l'errore
        } finally {
            setIsUploading(false);
        }
    };

    // Delete message
    const deleteMessage = (id) => {
        const updatedMessages = messages.filter(msg => msg.id !== id);
        saveMessages(updatedMessages);
    };

    // Sync messages from Google Drive (optional)
    const syncFromDrive = async () => {
        if (!driveServiceRef.current) return;
        
        try {
            setSubmitStatus('uploading');
            setUploadMessage('Sincronizzando da Google Drive...');
            const driveMessages = await driveServiceRef.current.syncMessagesFromDrive();
            
            console.log('📥 Dati ricevuti da Drive:', driveMessages);
            console.log('📊 Tipo dati:', typeof driveMessages, 'Array:', Array.isArray(driveMessages));
            
            // Validazione dei dati ricevuti
            if (!driveMessages) {
                console.warn('⚠️ Nessun dato ricevuto da Drive');
                setSubmitStatus('success');
                setUploadMessage('Nessun messaggio trovato su Drive');
                setTimeout(() => {
                    setSubmitStatus(null);
                    setUploadMessage('');
                }, 3000);
                return;
            }
            
            // Assicurati che sia un array
            const driveMessagesArray = Array.isArray(driveMessages) ? driveMessages : [];
            
            if (driveMessagesArray.length === 0) {
                console.log('ℹ️ Nessun messaggio trovato su Drive');
                setSubmitStatus('success');
                setUploadMessage('Nessun messaggio trovato su Drive');
                setTimeout(() => {
                    setSubmitStatus(null);
                    setUploadMessage('');
                }, 3000);
                return;
            }
            
            // Merge con i messaggi locali (evita duplicati)
            const localMessages = JSON.parse(localStorage.getItem('guestBookMessages') || '[]');
            const allMessages = [...driveMessagesArray, ...localMessages];
            
            console.log('📊 Messaggi totali prima deduplicazione:', allMessages.length);
            
            // Rimuovi duplicati basandoti sull'ID
            const uniqueMessages = allMessages.filter((msg, index, self) => 
                index === self.findIndex(m => m.id === msg.id)
            );
            
            console.log('📊 Messaggi unici dopo deduplicazione:', uniqueMessages.length);
            
            // Ordina per timestamp
            uniqueMessages.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            
            saveMessages(uniqueMessages);
            setSubmitStatus('success');
            setUploadMessage(`Sincronizzazione completata! ${driveMessagesArray.length} messaggi da Drive, ${uniqueMessages.length} totali.`);
            
            setTimeout(() => {
                setSubmitStatus(null);
                setUploadMessage('');
            }, 3000); // Ridotto da 5s a 3s
            
        } catch (error) {
            console.error('Errore sincronizzazione:', error);
            setSubmitStatus('error');
            setUploadMessage(`Errore nella sincronizzazione: ${error.message}`);
            setTimeout(() => {
                setSubmitStatus(null);
                setUploadMessage('');
            }, 3000); // Ridotto da 5s a 3s
        }
    };

    // Get upload statistics
    const getUploadStats = () => {
        if (!driveServiceRef.current) return null;
        return driveServiceRef.current.getUploadStats();
    };

    // Test connection with Google Apps Script
    const testConnection = async () => {
        if (!driveServiceRef.current) return;
        
        try {
            setSubmitStatus('uploading');
            setUploadMessage('Testando connessione...');
            await driveServiceRef.current.testConnection();
            setSubmitStatus('success');
            setUploadMessage('Test completato! Controlla la console per i dettagli.');
            setTimeout(() => {
                setSubmitStatus(null);
                setUploadMessage('');
            }, 3000);
        } catch (error) {
            setSubmitStatus('error');
            setUploadMessage('Errore nel test. Controlla la console.');
            setTimeout(() => {
                setSubmitStatus(null);
                setUploadMessage('');
            }, 3000);
        }
    };

    // Run full diagnostic
    const runDiagnostic = async () => {
        if (!driveServiceRef.current) return;
        
        try {
            setSubmitStatus('uploading');
            setUploadMessage('Eseguendo diagnostica completa...');
            await driveServiceRef.current.runDiagnostic();
            setSubmitStatus('success');
            setUploadMessage('Diagnostica completata! Controlla la console per i risultati dettagliati.');
            setTimeout(() => {
                setSubmitStatus(null);
                setUploadMessage('');
            }, 3000); // Ridotto da 5s a 3s
        } catch (error) {
            setSubmitStatus('error');
            setUploadMessage('Errore nella diagnostica. Controlla la console.');
            setTimeout(() => {
                setSubmitStatus(null);
                setUploadMessage('');
            }, 3000); // Ridotto da 5s a 3s
        }
    };

    // Clean storage when quota exceeded
    const cleanStorage = () => {
        try {
            localStorage.removeItem('guestBookMessages');
            localStorage.removeItem('pendingGuestBookUploads');
            setMessages([]);
            setSubmitStatus('success');
            setUploadMessage('Cache pulita! Ora puoi aggiungere nuovi messaggi.');
            setTimeout(() => {
                setSubmitStatus(null);
                setUploadMessage('');
            }, 3000);
        } catch (error) {
            console.error('Errore nella pulizia:', error);
        }
    };

    return (
        <div className="guest-book-section">
            <div className="content-container">
                <div className="guest-book-header" ref={formHeaderRef}>
                    <h2 className="guest-book-title font uppercase">
                        Libro degli Ospiti Digitale
                    </h2>
                    <p className="guest-book-subtitle font-detail">
                        Lascia un messaggio speciale! 
                        Scrivi, registra la tua voce o condividi una foto ricordo ✨
                    </p>
                    
                    {/* 
                    <div className="guest-stats">
                        <div className="stat-item">
                            <span className="stat-number">{messages.length}</span>
                            <span className="stat-label">Messaggi</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-number">{messages.filter(m => m.type === 'audio').length}</span>
                            <span className="stat-label">Audio</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-number">{messages.filter(m => m.type === 'photo').length}</span>
                            <span className="stat-label">Foto</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-number">{new Set(messages.map(m => m.name)).size}</span>
                            <span className="stat-label">Ospiti</span>
                        </div>
                    </div>  */}
                </div>

                {/* Tab Navigation */}
                <div className="guest-book-tabs">
                    <button 
                        className={`tab-btn ${activeTab === 'write' ? 'active' : ''} ${isUploading ? 'disabled' : ''}`}
                        onClick={() => !isUploading && setActiveTab('write')}
                        disabled={isUploading}
                        title={isUploading ? 'Attendi il completamento dell\'invio' : ''}
                    >
                        <span className='uppercase'>Scrivi</span>
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === 'audio' ? 'active' : ''} ${isUploading ? 'disabled' : ''}`}
                        onClick={() => !isUploading && setActiveTab('audio')}
                        disabled={isUploading}
                        title={isUploading ? 'Attendi il completamento dell\'invio' : ''}
                    >
                        <span className='uppercase'>Registra</span>
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === 'photo' ? 'active' : ''} ${isUploading ? 'disabled' : ''}`}
                        onClick={() => !isUploading && setActiveTab('photo')}
                        disabled={isUploading}
                        title={isUploading ? 'Attendi il completamento dell\'invio' : ''}
                    >
                        <span className='uppercase'>Foto</span>
                    </button>
                </div>

                {/* Upload status messages - Right after tabs */}
                {submitStatus === 'success' && (
                    <div className="success-message">
                        <div className="success-message-content">
                            <span className="success-text">
                                {uploadMessage}
                            </span>
                        </div>
                    </div>
                )}
                
                {/* Upload in progress indicator */}
                {isUploading && (
                    <div className="upload-progress-indicator">
                        <div className="upload-progress-content">
                            <div className="upload-spinner"></div>
                            <span className="upload-progress-text">
                                {uploadMessage || 'Caricamento in corso...'}
                            </span>
                        </div>
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="guest-book-form">
                    <div className="form-group">
                        <label className="form-label font">Inserisci il tuo Nome e Cognome *</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Il tuo Nome e Cognome"
                            className="form-input font-detail"
                            disabled={isUploading}
                            required
                        />
                    </div>

                    {/* Write Tab Content */}
                    {activeTab === 'write' && (
                        <div className="tab-content">
                            <div className="form-group">
                                <label className="form-label font">Scrivi il tuo messaggio</label>
                                
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    placeholder=""
                                    className="form-textarea font-detail"
                                    rows="5"
                                    disabled={isUploading}
                                    required
                                />
                                
                                <div className="character-count">
                                    {formData.message.length}/500 caratteri
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Audio Tab Content */}
                    {activeTab === 'audio' && (
                        <div className="tab-content">
                            <div className="audio-recorder">
                                <div className="recorder-display">
                                    {isRecording ? (
                                        <div className="recording-layout-vertical">
                                            <div className="recording-indicator">
                                                <div className="pulse-dot"></div>
                                                <span>Registrando... {formatTime(recordingTime)}</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    stopRecording();
                                                }}
                                                className="stop-btn-compact"
                                                disabled={isUploading}
                                                title="Ferma la registrazione"
                                            >
                                                Ferma
                                            </button>
                                        </div>
                                    ) : audioBlob && audioPreviewUrl ? (
                                        <div className="modern-audio-preview">
                                            <div className="audio-preview-header">
                                                <div className="audio-preview-status">
                                                    <div className="status-text">
                                                        <span className="status-title">Registrazione completata!</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="custom-audio-preview">
                                                <AudioMessage 
                                                    audioData={audioPreviewUrl} 
                                                    messageId={`preview-${Date.now()}`}
                                                    recordedDuration={recordingTime}
                                                />
                                            </div>
                                                                                            <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        startNewRecording();
                                                    }}
                                                    className="retry-audio-btn"
                                                    disabled={isUploading}
                                                    title="Registra nuovamente"
                                                >
                                                    Registra nuovamente
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="record-prompt-centered">
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    startRecording();
                                                }}
                                                className="record-btn"
                                                disabled={isUploading}
                                            >
                                                Registra il tuo messaggio
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Photo Tab Content */}
                    {activeTab === 'photo' && (
                        <div className="tab-content">
                            <div className="photo-upload">
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handlePhotoUpload}
                                    accept="image/*"
                                    multiple
                                    style={{ display: 'none' }}
                                />
                                
                                {formData.photos.length > 0 ? (
                                    <div className="photos-preview">
                                        <div className="photos-grid">
                                            {formData.photos.map((photo) => (
                                                <div key={photo.id} className="photo-preview-item">
                                                    <img src={photo.data} alt={`Preview ${photo.name}`} className="preview-image" />
                                                    <button
                                                        type="button"
                                                        onClick={() => removePhoto(photo.id)}
                                                        className="remove-photo-btn"
                                                        disabled={isUploading}
                                                        title="Rimuovi foto"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="photo-actions">
                                            <div className="photo-actions-buttons">
                                                <button
                                                    type="button"
                                                    onClick={() => fileInputRef.current.click()}
                                                    className="add-more-photos-btn"
                                                    disabled={isUploading || isProcessingPhotos || formData.photos.length >= 15}
                                                >
                                                    {formData.photos.length >= 15 ? 'Limite raggiunto (15 foto)' : 'Aggiungi altre foto'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={removeAllPhotos}
                                                    className="remove-all-photos-btn"
                                                    disabled={isUploading || isProcessingPhotos || formData.photos.length === 0}
                                                    title="Elimina tutte le foto"
                                                >
                                                    🗑️ Elimina tutte
                                                </button>
                                            </div>
                                            <span className="photo-count">
                                                {formData.photos.length}/15 foto
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div 
                                        className={`photo-upload-area ${isUploading || isProcessingPhotos ? 'disabled' : ''}`}
                                        onClick={() => !isUploading && !isProcessingPhotos && fileInputRef.current.click()}
                                        style={isUploading || isProcessingPhotos ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                                    >
                                        <span className="upload-text">Clicca per caricare foto</span>
                                        <span className="upload-subtext">Max 15 foto alla volta</span>
                                    </div>
                                )}
                                
                                {/* Photo Processing Loader */}
                                {isProcessingPhotos && (
                                    <div className="photo-processing-loader">
                                        <div className="photo-processing-content">
                                            <div className="photo-processing-spinner">
                                                <div className="spinner-dots">
                                                    <div></div>
                                                    <div></div>
                                                    <div></div>
                                                </div>
                                            </div>
                                            <div className="photo-processing-text">
                                                <span className="processing-title">Elaborazione foto in corso...</span>
                                                <span className="processing-progress">
                                                    {photoProcessingProgress.current} di {photoProcessingProgress.total} foto
                                                </span>
                                                <div className="processing-bar">
                                                    <div 
                                                        className="processing-bar-fill"
                                                        style={{ 
                                                            width: `${(photoProcessingProgress.current / photoProcessingProgress.total) * 100}%` 
                                                        }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                {/*<div className="form-group">
                                    <label className="form-label font">Descrivi la foto (opzionale)</label>
                                    <textarea
                                        name="message"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        placeholder="Racconta la storia di questa foto..."
                                        className="form-textarea"
                                        rows="3"
                                    />
                                </div>*/}
                            </div>
                        </div>
                    )}

                    {/* Mostra il pulsante solo se non c'è l'indicatore di progresso prominente */}
                    {!isUploading && (
                        <button 
                            type="submit" 
                            className="slide-button font uppercase"
                            disabled={!isFormValid()}
                        >
                            Invia ✨
                        </button>
                    )}

                    {/* Error status - Always show errors */}
                    {submitStatus === 'error' && (
                        <div className="error-message">
                            {uploadMessage}
                        </div>
                    )}
                    
                    {/* Upload status semplice - Solo se non c'è l'indicatore prominente */}
                    {submitStatus === 'uploading' && !isUploading && (
                        <div className="upload-status-simple">
                            {uploadMessage}
                        </div>
                    )}
                </form>

                {/* Debug Panel - Show only when needed */}
                {window.location.search.includes('debug') && (
                    <div className="debug-panel" style={{
                        marginTop: '30px',
                        padding: '20px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px'
                    }}>
                        <h4 style={{ color: '#6c757d', marginBottom: '15px' }}>🔧 Debug Panel</h4>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button 
                                onClick={testConnection}
                                className="debug-btn"
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Test Connessione
                            </button>
                            <button 
                                onClick={runDiagnostic}
                                className="debug-btn"
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Diagnostica Completa
                            </button>
                            <button 
                                onClick={syncFromDrive}
                                className="debug-btn"
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#17a2b8',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Sync da Drive
                            </button>
                            <button 
                                onClick={cleanStorage}
                                className="debug-btn"
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Pulisci Cache
                            </button>
                            
                            <button 
                                onClick={() => {
                                    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                                                 (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
                                    
                                    console.log('🍎 === DIAGNOSTICA iOS COMPLETA ===');
                                    console.log('Dispositivo iOS rilevato:', isIOS);
                                    console.log('User Agent:', navigator.userAgent);
                                    console.log('Platform:', navigator.platform);
                                    console.log('Touch Points:', navigator.maxTouchPoints);
                                    
                                    // Testa i formati audio supportati per registrazione
                                    const recordingFormats = [
                                        'audio/mp4',
                                        'audio/wav',
                                        'audio/webm',
                                        'audio/webm;codecs=opus',
                                        'audio/ogg;codecs=opus'
                                    ];
                                    
                                    console.log('🎵 Formati audio supportati per REGISTRAZIONE:');
                                    recordingFormats.forEach(format => {
                                        const supported = MediaRecorder.isTypeSupported(format);
                                        console.log(`  ${format}: ${supported ? '✅' : '❌'}`);
                                    });
                                    
                                    // Testa i formati audio supportati per riproduzione
                                    const playbackFormats = [
                                        'audio/mp4',
                                        'audio/wav', 
                                        'audio/webm',
                                        'audio/ogg'
                                    ];
                                    
                                    console.log('🔊 Formati audio supportati per RIPRODUZIONE:');
                                    const testAudio = new Audio();
                                    playbackFormats.forEach(format => {
                                        const canPlay = testAudio.canPlayType(format);
                                        console.log(`  ${format}: ${canPlay === 'probably' ? '✅ Probably' : canPlay === 'maybe' ? '⚠️ Maybe' : '❌ No'}`);
                                    });
                                    
                                    // Testa i permessi
                                    if (navigator.permissions) {
                                        navigator.permissions.query({name: 'microphone'}).then(result => {
                                            console.log('🎤 Permessi microfono:', result.state);
                                        }).catch(err => {
                                            console.log('❌ Impossibile verificare permessi:', err);
                                        });
                                    }
                                    
                                    // Informazioni sulla versione Safari/iOS
                                    if (isIOS) {
                                        const match = navigator.userAgent.match(/Version\/(\d+\.\d+)/);
                                        const safariVersion = match ? match[1] : 'Sconosciuta';
                                        console.log('🧭 Versione Safari:', safariVersion);
                                        
                                        const iosMatch = navigator.userAgent.match(/OS (\d+)_(\d+)/);
                                        const iosVersion = iosMatch ? `${iosMatch[1]}.${iosMatch[2]}` : 'Sconosciuta';
                                        console.log('📱 Versione iOS:', iosVersion);
                                    }
                                    
                                    alert('Diagnostica iOS completata. Controlla la console per i dettagli completi.');
                                }}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#ff9500',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                🍎 Test iOS
                            </button>
                        </div>
                        <p style={{ 
                            marginTop: '10px', 
                            fontSize: '0.8rem', 
                            color: '#6c757d' 
                        }}>
                            💡 Aggiungi ?debug all'URL per vedere questo panel
                        </p>
                    </div>
                )}

                {/* Messages Display - Hidden for cleaner UI */}
                {false && messages.length > 0 && (
                    <div className="messages-section">
                        <div className="messages-header">
                            <h3 className="messages-title font uppercase">
                                Messaggi degli Ospiti ({filteredMessages.length}/{messages.length})
                            </h3>
                           
                        </div>
                        
                        {/* Filtri e Ricerca */}
                        <div className="messages-filters">
                            <div className="filter-section">
                                <label className="filter-label">Filtra per tipo:</label>
                                <div className="filter-buttons">
                                    <button 
                                        className={`filter-btn ${messageFilter === 'all' ? 'active' : ''}`}
                                        onClick={() => setMessageFilter('all')}
                                    >
                                        Tutti ({messages.length})
                                    </button>
                                    <button 
                                        className={`filter-btn ${messageFilter === 'write' ? 'active' : ''}`}
                                        onClick={() => setMessageFilter('write')}
                                    >
                                        ✍️ Testi ({messages.filter(m => m.type === 'write').length})
                                    </button>
                                    <button 
                                        className={`filter-btn ${messageFilter === 'audio' ? 'active' : ''}`}
                                        onClick={() => setMessageFilter('audio')}
                                    >
                                        🎤 Audio ({messages.filter(m => m.type === 'audio').length})
                                    </button>
                                    <button 
                                        className={`filter-btn ${messageFilter === 'photo' ? 'active' : ''}`}
                                        onClick={() => setMessageFilter('photo')}
                                    >
                                        📷 Foto ({messages.filter(m => m.type === 'photo').length})
                                    </button>
                                </div>
                            </div>
                            
                            <div className="search-section">
                                <label className="filter-label">Cerca ospite o messaggio:</label>
                                <input
                                    type="text"
                                    placeholder="Cerca per nome o contenuto..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="search-input"
                                />
                            </div>
                        </div>
                        
                        <div className="messages-grid">
                            {filteredMessages.map((msg) => (
                                <div key={msg.id} className="message-card">
                                    <div className="message-header">
                                        <div className="message-info">
                                            <span className="message-name font bold">{msg.name}</span>
                                            <span className="message-time font-detail">{msg.timestamp}</span>
                                        </div>
                                        <div className="message-type">
                                            {msg.type === 'write' && '✍️'}
                                            {msg.type === 'audio' && '🎤'}
                                            {msg.type === 'photo' && '📷'}
                                        </div>
                                    </div>
                                    
                                    <div className="message-content">
                                        {msg.type === 'audio' && msg.audioData && (
                                            <AudioMessage 
                                                audioData={msg.audioData} 
                                                messageId={msg.id}
                                                recordedDuration={msg.recordedDuration}
                                            />
                                        )}
                                        
                                        {(msg.photos?.length > 0 || msg.photo) && (
                                            <div className="message-photos">
                                                {msg.photos?.length > 0 ? (
                                                    msg.photos.map((photo, index) => (
                                                        <img 
                                                            key={index} 
                                                            src={photo.data || photo} 
                                                            alt={`Guest photo ${index + 1}`} 
                                                            className="message-photo" 
                                                        />
                                                    ))
                                                ) : msg.photo && (
                                                    <img src={msg.photo} alt="Guest photo" className="message-photo" />
                                                )}
                                            </div>
                                        )}
                                        
                                        {msg.message && (
                                            <p className="message-text font-detail">{msg.message}</p>
                                        )}
                                    </div>
                                    
                                    <button 
                                        onClick={() => deleteMessage(msg.id)}
                                        className="delete-btn"
                                        title="Elimina messaggio"
                                    >
                                        🗑️
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GuestBook; 
